import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Loader2, Tag, Trash2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PromoCode, PromoUsageRow } from "./types";

export function PromoCodesTab() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [usageCodeId, setUsageCodeId] = useState<number | null>(null);
  const [form, setForm] = useState({
    code: "",
    discountType: "percent" as "percent" | "fixed",
    discountValue: "",
    duration: "forever" as "forever" | "repeating",
    durationMonths: "",
    maxRedemptions: "",
    expiresAt: "",
  });

  const codesQuery = useQuery<PromoCode[]>({
    queryKey: ["/api/admin/promo-codes"],
  });

  const usageQuery = useQuery<PromoUsageRow[]>({
    queryKey: ["/api/admin/promo-codes", usageCodeId, "usage"],
    queryFn: () =>
      apiRequest("GET", `/api/admin/promo-codes/${usageCodeId}/usage`).then((r) => r.json()),
    enabled: usageCodeId !== null,
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof form) => {
      const payload: Record<string, unknown> = {
        code: data.code.toUpperCase().trim(),
        discountType: data.discountType,
        discountValue: Number(data.discountValue),
        duration: data.duration,
      };
      if (data.duration === "repeating" && data.durationMonths) {
        payload.durationMonths = Number(data.durationMonths);
      }
      if (data.maxRedemptions) payload.maxRedemptions = Number(data.maxRedemptions);
      if (data.expiresAt) payload.expiresAt = new Date(data.expiresAt).toISOString();
      const res = await apiRequest("POST", "/api/admin/promo-codes", payload);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/promo-codes"] });
      setCreateOpen(false);
      setForm({ code: "", discountType: "percent", discountValue: "", duration: "forever", durationMonths: "", maxRedemptions: "", expiresAt: "" });
      toast({ title: "Promo code created" });
    },
    onError: (err: unknown) => {
      toast({ title: "Error", description: parseErrorMessage(err, "Could not create promo code."), variant: "destructive" });
    },
  });

  const removeUsageMutation = useMutation({
    mutationFn: async ({ promoId, userId }: { promoId: number; userId: number }) => {
      const res = await apiRequest("DELETE", `/api/admin/promo-codes/${promoId}/usage/${userId}`);
      return res.json() as Promise<{ success: boolean; stripeRemoved: boolean; stripeWarning: string | null }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/promo-codes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/promo-codes", usageCodeId, "usage"] });
      if (data.stripeWarning) {
        toast({
          title: "Removed locally",
          description: `Cleared in PadPulse but Stripe update failed: ${data.stripeWarning}. Adjust the subscription discount in the Stripe dashboard.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Promo removed",
          description: data.stripeRemoved
            ? "Promo cleared from the host's account and Stripe subscription."
            : "Promo cleared from the host's account.",
        });
      }
    },
    onError: (err: unknown) => {
      toast({ title: "Error", description: parseErrorMessage(err, "Could not remove promo code."), variant: "destructive" });
    },
  });

  const [deleteCodeId, setDeleteCodeId] = useState<number | null>(null);

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: number; isActive: boolean }) => {
      const res = await apiRequest("PATCH", `/api/admin/promo-codes/${id}`, { isActive });
      return res.json();
    },
    onSuccess: (data: { stripeWarning?: string | null } | undefined, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/promo-codes"] });
      const title = variables.isActive ? "Promo code activated" : "Promo code deactivated";
      if (data?.stripeWarning) {
        toast({
          title,
          description: `Stripe update failed: ${data.stripeWarning}. Please update the coupon in the Stripe Dashboard manually.`,
          variant: "destructive",
        });
      } else {
        toast({ title });
      }
    },
    onError: (err: unknown) => {
      toast({ title: "Error", description: parseErrorMessage(err, "Could not update promo code."), variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("DELETE", `/api/admin/promo-codes/${id}`);
      if (res.status === 204) return null;
      return res.json().catch(() => null);
    },
    onSuccess: (data: { stripeWarning?: string | null } | null) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/promo-codes"] });
      setDeleteCodeId(null);
      if (data?.stripeWarning) {
        toast({
          title: "Promo code deleted",
          description: `Stripe coupon cleanup failed: ${data.stripeWarning}. Please remove the coupon in the Stripe Dashboard manually.`,
          variant: "destructive",
        });
      } else {
        toast({ title: "Promo code deleted" });
      }
    },
    onError: (err: unknown) => {
      setDeleteCodeId(null);
      toast({ title: "Error", description: parseErrorMessage(err, "Could not delete promo code."), variant: "destructive" });
    },
  });

  const formatDiscount = (pc: PromoCode) => {
    const val = Number(pc.discountValue);
    return pc.discountType === "percent" ? `${val}% off` : `$${val.toFixed(2)} off`;
  };
  const formatDuration = (pc: PromoCode) => {
    if (pc.duration === "forever") return "Forever";
    return `${pc.durationMonths} month${pc.durationMonths === 1 ? "" : "s"}`;
  };

  const selectedCode = codesQuery.data?.find((c) => c.id === usageCodeId);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Promo Codes</h2>
          <p className="text-sm text-muted-foreground mt-1">Create and manage promotional discount codes for hosts.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="button-create-promo-code" data-tour="admin-promo-codes-new">
          <Tag className="h-4 w-4 mr-2" />
          New Promo Code
        </Button>
      </div>

      {codesQuery.isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      ) : !codesQuery.data?.length ? (
        <Card className="p-8 text-center text-muted-foreground">
          <Tag className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium">No promo codes yet</p>
          <p className="text-sm mt-1">Create a promo code to give hosts a discount on their subscription.</p>
        </Card>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-separate border-spacing-y-1" data-testid="table-promo-codes" data-tour="admin-promo-codes-table">
            <thead>
              <tr className="text-muted-foreground text-xs uppercase tracking-wide">
                <th className="text-left py-2 px-3">Code</th>
                <th className="text-left py-2 px-3">Discount</th>
                <th className="text-left py-2 px-3">Duration</th>
                <th className="text-left py-2 px-3">Redemptions</th>
                <th className="text-left py-2 px-3">Expires</th>
                <th className="text-left py-2 px-3">Stripe</th>
                <th className="text-left py-2 px-3">Active</th>
                <th className="text-left py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {codesQuery.data.map((pc) => {
                const isExpired = pc.expiresAt ? new Date(pc.expiresAt) < new Date() : false;
                const isCapped = pc.maxRedemptions !== null && pc.currentRedemptions >= pc.maxRedemptions;
                const canDelete = pc.currentRedemptions === 0;
                return (
                  <tr
                    key={pc.id}
                    className="bg-card hover:bg-muted/50 rounded-lg transition-colors"
                    data-testid={`row-promo-${pc.id}`}
                  >
                    <td className="py-3 px-3 rounded-l-lg">
                      <span className="font-mono font-semibold text-sm tracking-wider">{pc.code}</span>
                      {(isExpired || isCapped || !pc.isActive) && (
                        <Badge variant="destructive" className="ml-2 text-xs">
                          {!pc.isActive ? "Inactive" : isExpired ? "Expired" : "Capped"}
                        </Badge>
                      )}
                    </td>
                    <td className="py-3 px-3">{formatDiscount(pc)}</td>
                    <td className="py-3 px-3">{formatDuration(pc)}</td>
                    <td className="py-3 px-3 tabular-nums">
                      {pc.currentRedemptions}
                      {pc.maxRedemptions !== null ? ` / ${pc.maxRedemptions}` : ""}
                    </td>
                    <td className="py-3 px-3">
                      {pc.expiresAt ? new Date(pc.expiresAt).toLocaleDateString() : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="py-3 px-3">
                      {pc.stripePromotionCodeId ? (
                        <Badge variant="secondary" className="text-xs">Synced</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">Local only</Badge>
                      )}
                    </td>
                    <td className="py-3 px-3">
                      <Switch
                        checked={pc.isActive}
                        disabled={toggleActiveMutation.isPending}
                        onCheckedChange={(v) => toggleActiveMutation.mutate({ id: pc.id, isActive: v })}
                        data-testid={`switch-promo-active-${pc.id}`}
                      />
                    </td>
                    <td className="py-3 px-3 rounded-r-lg">
                      <div className="flex items-center gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setUsageCodeId(usageCodeId === pc.id ? null : pc.id)}
                          data-testid={`button-promo-usage-${pc.id}`}
                        >
                          Usage
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                          disabled={!canDelete}
                          title={canDelete ? "Delete this promo code" : "Cannot delete — code has been redeemed. Deactivate it instead."}
                          onClick={() => setDeleteCodeId(pc.id)}
                          data-testid={`button-promo-delete-${pc.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {usageCodeId !== null && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm">Usage: {selectedCode?.code}</h3>
            <Button variant="ghost" size="sm" onClick={() => setUsageCodeId(null)}>Close</Button>
          </div>
          {usageQuery.isLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : !usageQuery.data?.length ? (
            <p className="text-sm text-muted-foreground">No redemptions yet.</p>
          ) : (
            <div className="space-y-1">
              {usageQuery.data.map((row) => {
                const isRemoving =
                  removeUsageMutation.isPending &&
                  removeUsageMutation.variables?.userId === row.userId &&
                  removeUsageMutation.variables?.promoId === row.promoCodeId;
                return (
                  <div key={row.id} className="flex items-center justify-between gap-3 text-sm py-1.5 border-b last:border-0" data-testid={`row-promo-usage-${row.id}`}>
                    <span className="truncate">
                      {row.userName ? `${row.userName} (${row.userEmail})` : row.userEmail}
                    </span>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-muted-foreground text-xs">{new Date(row.appliedAt).toLocaleDateString()}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        disabled={isRemoving}
                        onClick={() => {
                          if (!confirm(`Remove promo ${selectedCode?.code ?? ""} from ${row.userEmail}? This will clear the discount on their account and Stripe subscription.`)) return;
                          removeUsageMutation.mutate({ promoId: row.promoCodeId, userId: row.userId });
                        }}
                        data-testid={`button-promo-remove-${row.id}`}
                      >
                        {isRemoving ? "Removing…" : "Remove"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="dialog-create-promo-code">
          <DialogHeader>
            <DialogTitle>New Promo Code</DialogTitle>
            <DialogDescription>Creates a Stripe coupon + promotion code and saves it in PadPulse.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Code <span className="text-muted-foreground text-xs">(uppercase, letters/numbers/dashes)</span></Label>
              <Input
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="SUMMER20"
                data-testid="input-promo-code"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Discount type</Label>
                <Select value={form.discountType} onValueChange={(v) => setForm((f) => ({ ...f, discountType: v as "percent" | "fixed" }))}>
                  <SelectTrigger data-testid="select-promo-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percent (%)</SelectItem>
                    <SelectItem value="fixed">Fixed ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Value</Label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={form.discountValue}
                  onChange={(e) => setForm((f) => ({ ...f, discountValue: e.target.value }))}
                  placeholder={form.discountType === "percent" ? "20" : "10.00"}
                  data-testid="input-promo-value"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>How long the discount lasts</Label>
                <Select value={form.duration} onValueChange={(v) => setForm((f) => ({ ...f, duration: v as "forever" | "repeating" }))}>
                  <SelectTrigger data-testid="select-promo-duration"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="forever">Forever — applies every month until subscription is canceled</SelectItem>
                    <SelectItem value="repeating">Limited — applies for the first N months, then ends</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {form.duration === "forever"
                    ? "Example: 20% off every invoice for as long as the host stays subscribed."
                    : "Example: 20% off the first 3 invoices only, then the host pays full price. This is NOT a discount that repeats every N months."}
                </p>
              </div>
              {form.duration === "repeating" && (
                <div className="space-y-1.5">
                  <Label>Number of months to apply discount</Label>
                  <Input
                    type="number"
                    min="1"
                    value={form.durationMonths}
                    onChange={(e) => setForm((f) => ({ ...f, durationMonths: e.target.value }))}
                    placeholder="3"
                    data-testid="input-promo-months"
                  />
                  <p className="text-xs text-muted-foreground">
                    Total billing cycles the discount applies before it ends.
                  </p>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Max redemptions <span className="text-muted-foreground text-xs">(blank = unlimited)</span></Label>
                <Input
                  type="number"
                  min="1"
                  value={form.maxRedemptions}
                  onChange={(e) => setForm((f) => ({ ...f, maxRedemptions: e.target.value }))}
                  placeholder="∞"
                  data-testid="input-promo-max-redemptions"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Expires at <span className="text-muted-foreground text-xs">(optional)</span></Label>
                <Input
                  type="date"
                  value={form.expiresAt}
                  onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))}
                  data-testid="input-promo-expires"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={() => createMutation.mutate(form)}
              disabled={createMutation.isPending || !form.code || !form.discountValue}
              data-testid="button-promo-submit"
            >
              {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Tag className="h-4 w-4 mr-2" />}
              Create Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteCodeId !== null} onOpenChange={(v) => !v && setDeleteCodeId(null)}>
        <AlertDialogContent data-testid="dialog-delete-promo">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this promo code?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the code from PadPulse. The matching Stripe coupon and promotion code are not deleted automatically — you may want to archive them in the Stripe Dashboard. Codes that have already been redeemed cannot be deleted; deactivate them instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-promo-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteCodeId !== null && deleteMutation.mutate(deleteCodeId)}
              disabled={deleteMutation.isPending}
              data-testid="button-promo-delete-confirm"
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
